import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classes from './Notifications.module.scss';
import {
  BulkMarkNotificationsAsReadMutationFn,
  GetNotificationsQuery,
  GetNotificationsQueryResult,
  MarkAllNotificationsAsReadMutationFn,
  Notification,
  NotificationName
} from '../../../models/graphql';
import { formatTimeDiffShort } from '../../../helpers/timeFormatter';
import { Loader } from '../../../components/containers/Loader';
import classNames from 'classnames';
import { SortFunction } from '../../../models/basic-objects';
import { cloneDeep } from 'lodash';
import { NotificationsPaginationStatus } from './Notifications.controller';
import { PostScheduleFiltersEnum, setFilterLocalStorage } from '../../../helpers/filterStorageHelpers';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import { useLocation, useNavigate } from 'react-router-dom';
import { setUnreadNotificationsQuery } from './Notifications.apollo';
import { useApolloClient } from '@apollo/client';
import { useDidMount } from '../../../hooks/useDidMount';
import { PostScheduleFilterType } from '../../../models/post';
import { BeaconContext } from '../../../providers/BeaconProvider';

export type NotificationsViewProps = {
  notificationsResult: GetNotificationsQueryResult;
  paginate: ((event: React.UIEvent<HTMLDivElement, UIEvent>) => void) | undefined;
  markAsRead: BulkMarkNotificationsAsReadMutationFn;
  markAllAsRead: MarkAllNotificationsAsReadMutationFn;
  selectedOrganizationId: string;
  selectedWorkspaceId: string;
  paginationStatus: NotificationsPaginationStatus;
};

const sortNotifications: SortFunction<GetNotificationsQuery['notifications']['items'][number]> = (a, b) => {
  if (a.isRead && !b.isRead) return 1;
  if (!a.isRead && b.isRead) return -1;
  if (a.timestamp > b.timestamp) return -1;
  if (a.timestamp < b.timestamp) return 1;
  return 0;
};

const getNotificationTitle = (name: NotificationName): string => {
  switch (name) {
    case NotificationName.ACCOUNT_SPAM_LOCKED:
      return 'Account Spam Locked';
    case NotificationName.ACCOUNT_SUSPENDED:
      return 'Account Suspended';
    case NotificationName.ACCOUNT_UNLOCKED:
      return 'Account Unlocked';
    case NotificationName.AUTO_IMPORTER_CATEGORY_ARCHIVED:
      return 'Category Archived';
    case NotificationName.AUTO_IMPORTER_INVALID_SOURCE:
      return 'Invalid Source';
    case NotificationName.AUTO_IMPORTER_QUEUE_FULL:
      return 'Your Queue Is Full';
    case NotificationName.BITLY_FAILED:
      return 'Bitly Failed';
    case NotificationName.BULK_IMPORT_COMPLETED:
      return 'Bulk Import Completed';
    case NotificationName.BULK_IMPORT_FAILED:
      return 'Bulk Import Failed';
    case NotificationName.FACEBOOK_USER_DEAUTHORIZED:
      return 'Facebook User Deathorized';
    case NotificationName.AUTO_IMPORTER_PROFILES_REMOVED:
      return 'Profile(s) Removed';
    case NotificationName.BULK_IMPORT_PARTIALLY_FAILED:
      return 'Bulk Import Partially Failed';
    case NotificationName.INFO:
      return 'Notiication';
    case NotificationName.FIRST_COMMENT_FAILED:
      return 'First Comment Failed';
    case NotificationName.FOLLOWER_COUNTS_FAILED:
      return 'Follower Counts Failed';
    case NotificationName.INSTAGRAM_REEL_TOO_LONG:
      return 'Instagram Reel Failed';
    case NotificationName.LINKEDIN_TOKEN_EXPIRING_7_DAYS:
    case NotificationName.LINKEDIN_TOKEN_EXPIRING_2_DAYS:
      return 'LinkedIn Token';
    case NotificationName.POST_STATS_FAILED:
      return 'Post Stats Failed';
    case NotificationName.POST_FAILED_PROFILE_DISCONNECTED:
    case NotificationName.POST_REQUEUED_PROFILE_DISCONNECTED:
    case NotificationName.POST_FAILED:
      return 'Post Failed to Publish';
    case NotificationName.PROFILE_DISCONNECTED:
      return 'Profile Disconnected';
    case NotificationName.REFRESH_INSTAGRAM_TOKEN:
      return 'Instagram Token';
    case NotificationName.REFRESH_THREADS_TOKEN:
      return 'Threads Token';
    case NotificationName.INSTAGRAM_STORY_VIDEO_TOO_LONG:
    case NotificationName.LINKEDIN_VIDEO_TOO_SHORT:
    case NotificationName.FACEBOOK_VIDEO_TOO_LONG:
    case NotificationName.FACEBOOK_VIDEO_ASPECT_UNSUPPORTED:
    case NotificationName.TWITTER_VIDEO_ASPECT_UNSUPPORTED:
    case NotificationName.VIDEO_PROCESSING_FAILED:
    case NotificationName.TWITTER_VIDEO_TOO_LONG:
      return 'Video Failed';
    case NotificationName.DAILY_TWEET_CAP_EXCEEDED:
    case NotificationName.WEEKLY_TWEET_CAP_EXCEEDED:
      return 'Tweet Cap Reached';
    case NotificationName.LEGACY:
    default:
      return 'Notification';
  }
};

const noCtaNames: NotificationName[] = [
  NotificationName.FIRST_COMMENT_FAILED,
  NotificationName.ACCOUNT_UNLOCKED,
  NotificationName.BULK_IMPORT_COMPLETED,
  NotificationName.BULK_IMPORT_FAILED,
  NotificationName.AUTO_IMPORTER_INVALID_SOURCE,
  NotificationName.TWITTER_VIDEO_TOO_LONG,
  NotificationName.FACEBOOK_VIDEO_TOO_LONG,
  NotificationName.LINKEDIN_VIDEO_TOO_SHORT,
  NotificationName.INSTAGRAM_STORY_VIDEO_TOO_LONG,
  NotificationName.INSTAGRAM_REEL_TOO_LONG,
  NotificationName.FACEBOOK_VIDEO_ASPECT_UNSUPPORTED,
  NotificationName.TWITTER_VIDEO_ASPECT_UNSUPPORTED,
  NotificationName.VIDEO_PROCESSING_FAILED
];

const setPostFilters = (selectedWorkspaceId: string): void => {
  const filters = localStorage.getItem('PostScheduleFilters');
  if (!filters) {
    setFilterLocalStorage(
      selectedWorkspaceId,
      'PostScheduleFilters',
      PostScheduleFiltersEnum.postScheduleStatusFilter,
      'FAILED'
    );
  } else {
    const wsFilters: Record<string, Record<PostScheduleFiltersEnum, string>> | undefined = JSON.parse(filters);
    if (wsFilters && wsFilters[selectedWorkspaceId]) {
      wsFilters[selectedWorkspaceId] = {
        postScheduleStatusFilter: 'FAILED',
        postScheduleProfileFilter: '',
        postScheduleCategoryFilter: '',
        postScheduleTimingFilter: '',
        postScheduleEvergreenFilter: '',
        postScheduleSourceFilter: '',
        postScheduleContentFilter: ''
      };
      localStorage.setItem('PostScheduleFilters', JSON.stringify(wsFilters));
    }
  }
};

type ActionHandler = (id: string, isRead: boolean, type: NotificationName) => void;

type CtaResult = {
  action: VoidFunction;
  ctaLabel: string | null;
};

export const NotificationsView = (props: NotificationsViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const { markAsRead: markAsReadHandler } = props;
  const unreadCount = props.notificationsResult.data?.notifications.items.filter(item => !item.isRead).length;
  const [markedAsRead, setMarkedAsRead] = useState<string[]>([]);
  const hasUnread = !!unreadCount && markedAsRead.length < unreadCount;
  const navigate = useNavigate();
  const location = useLocation();
  const { cache } = useApolloClient();
  const markedAsReadRef = useRef(markedAsRead);

  const items = useMemo(() => {
    // we need to clone deep since data is read only and we can't sort read only array
    const copiedResults = props.notificationsResult.data
      ? cloneDeep(props.notificationsResult.data.notifications.items)
      : [];
    copiedResults.sort(sortNotifications);
    return copiedResults;
  }, [props.notificationsResult.data]);

  const markAsRead = (notificationId: Notification['id']) => {
    if (!notificationId) return;
    setUnreadNotificationsQuery('one', props.selectedOrganizationId, cache);
    setMarkedAsRead(old => [...old, notificationId]);
  };

  const handlePostFailure: ActionHandler = (id, isRead, _): void => {
    if (!isRead) markAsRead(id);
    const pathname = '/publish/queue';
    setPostFilters(props.selectedWorkspaceId);
    if (location.pathname.includes(pathname)) {
      const locationState: PostScheduleFilterType = {
        status: 'FAILED',
        profile: '',
        category: '',
        timing: '',
        evergreen: '',
        source: '',
        content: '',
        fetchPosts: true
      };
      navigate(
        {
          pathname
        },
        {
          state: locationState
        }
      );
    } else {
      navigate({ pathname });
    }
  };

  const handleSimpleGotoPage: ActionHandler = (id, isRead, name) => {
    if (!isRead) markAsRead(id);
    let pathname: string | null;
    switch (name) {
      case NotificationName.PROFILE_DISCONNECTED:
      case NotificationName.POST_REQUEUED_PROFILE_DISCONNECTED:
      case NotificationName.POST_STATS_FAILED:
      case NotificationName.FOLLOWER_COUNTS_FAILED:
      case NotificationName.LINKEDIN_TOKEN_EXPIRING_2_DAYS:
      case NotificationName.LINKEDIN_TOKEN_EXPIRING_7_DAYS:
        pathname = '/settings/profiles';
        break;
      case NotificationName.AUTO_IMPORTER_PROFILES_REMOVED:
        pathname = '/discover/auto';
        break;
      default:
        pathname = null;
        break;
    }
    !!pathname && navigate({ pathname });
  };

  const handleContactUs: ActionHandler = (id: string, isRead: boolean, _: NotificationName): void => {
    if (!isRead) markAsRead(id);
    beacon('open');
    beacon('navigate', '/ask');
  };

  const getCTA = (notification: GetNotificationsQuery['notifications']['items'][number]): JSX.Element | null => {
    const { name, isRead, id } = notification;
    if (noCtaNames.includes(name)) return null;
    let result: null | CtaResult;
    switch (name) {
      case NotificationName.POST_FAILED:
      case NotificationName.POST_FAILED_PROFILE_DISCONNECTED:
        result = {
          ctaLabel: 'Review failed post',
          action: () => handlePostFailure(id, isRead, name)
        };
        break;
      case NotificationName.PROFILE_DISCONNECTED:
      case NotificationName.POST_STATS_FAILED:
      case NotificationName.FOLLOWER_COUNTS_FAILED:
      case NotificationName.LINKEDIN_TOKEN_EXPIRING_2_DAYS:
      case NotificationName.LINKEDIN_TOKEN_EXPIRING_7_DAYS:
      case NotificationName.POST_REQUEUED_PROFILE_DISCONNECTED:
        result = {
          ctaLabel: 'Re-connect the profile',
          action: () => handleSimpleGotoPage(id, isRead, name)
        };
        break;
      case NotificationName.ACCOUNT_SPAM_LOCKED:
        result = {
          ctaLabel: 'Contact us to unlock your account',
          action: () => handleContactUs(id, isRead, name)
        };
        break;
      case NotificationName.ACCOUNT_SUSPENDED:
        result = {
          ctaLabel: 'Contact us if you wish to appeal',
          action: () => handleContactUs(id, isRead, name)
        };
        break;
      case NotificationName.BULK_IMPORT_PARTIALLY_FAILED:
        result = {
          ctaLabel: 'Review the posts in your Queue',
          action: () => handlePostFailure(id, isRead, name)
        };
        break;
      case NotificationName.AUTO_IMPORTER_PROFILES_REMOVED:
        result = {
          ctaLabel:
            'Go to the RSS Auto-Importer page and edit this RSS feed. Select Profiles to import to, and turn on auto-importing again.',
          action: () => handleSimpleGotoPage(id, isRead, name)
        };
        break;
      default:
        result = null;
        break;
    }
    return result ? (
      <div className={classes.NotificationMessage}>
        <a
          onClick={e => {
            e.stopPropagation();
            result && result.action();
          }}>
          {result.ctaLabel}
        </a>
      </div>
    ) : null;
  };

  const setAllAsRead = () => {
    setUnreadNotificationsQuery('all', props.selectedOrganizationId, cache);
    props.markAllAsRead().then(({ errors }) => {
      if (errors) {
        triggerErrorToast('Setting Notifications Read Failed')(errors);
      }
    });
  };

  // every time marked as read changes we need to update marked as read ref.
  useEffect(() => {
    markedAsReadRef.current = markedAsRead;
  }, [markedAsRead]);

  // when we unmount we need to trigger markedAsRead for each marked item. we need to rely on markedAsReadRef, since markedAsRead state value
  // has already been reset to initial value upon cleanup.
  useDidMount(() => () => {
    if (markedAsReadRef.current.length) {
      markAsReadHandler({ variables: { notificationIds: markedAsReadRef.current } }).then(({ errors }) => {
        if (errors) {
          triggerErrorToast('Setting Notification Read Failed')(errors);
        }
      });
    }
  });

  return (
    <>
      {hasUnread ? (
        <div className={classes.NotificationsMarkReadSection}>
          <a
            onClick={() => {
              setAllAsRead();
            }}>
            Mark all as read
          </a>{' '}
        </div>
      ) : null}
      <div
        onScroll={props.paginate}
        className={classNames(classes.Notifications, hasUnread ? classes.NotificationsHasUnread : '')}>
        {items.length > 0 ? (
          <ul className={classes.NotificationsContainer}>
            {items?.map(notification => {
              // Add data-notification_link to each message link so we can use react router with them.
              // Below, we render the raw HTML using dangerouslySetInnerHTML so the links don't get escaped.
              // This should be safe, but there may be a safer way to escape everything but links.
              const isRead = notification.isRead || !!(notification.id && markedAsRead.includes(notification.id));
              return (
                <li
                  onClick={() => !isRead && markAsRead(notification.id)}
                  key={notification.id}
                  className={`${classes.NotificationCard} ${isRead ? '' : classes.MessageUnread}`}>
                  <div className={'grid-x align-top'}>
                    <div className={'cell shrink'}>
                      {isRead ? null : (
                        <div className={classes.NotificationDot}>
                          <span className={'show-for-sr'}>*</span>
                        </div>
                      )}
                      <div className={classNames(classes.NotificationDate, isRead ? classes.NotificationDateRead : '')}>
                        {formatTimeDiffShort(notification.timestamp)}
                      </div>
                    </div>
                    <div className={'cell auto'}>
                      <div
                        className={classNames(classes.NotificationTitle, isRead ? classes.NotificationTitleRead : '')}>
                        {getNotificationTitle(notification.name)}
                      </div>
                      <div
                        className={classNames(
                          classes.NotificationMessage,
                          isRead ? classes.NotificationMessageRead : ''
                        )}>
                        {notification.message}
                      </div>
                      {getCTA(notification)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        {!items.length && !props.notificationsResult.loading ? (
          <div className={`grid-x align-center ${classes.NoNotificationsMessageWrapper}`}>
            <div className={`grid-y full ${classes.NoNotificationsMessage}`}>
              There are no notifications at the moment
            </div>
          </div>
        ) : null}
        {props.paginationStatus.isFetching ? (
          <div className={classes.LoadMoreWrapper}>
            <Loader size={'large'} type={'inline'} variation={'dark'} />
          </div>
        ) : null}
        {props.notificationsResult.loading ? <Loader size={'large'} type={'inline'} variation={'dark'} /> : null}
      </div>
    </>
  );
};
